import { cn } from '@/lib/utils';

interface IRadioCardProps {
  checked: boolean;
  onSelect: () => void;
  title: string;
  subtitle?: string;
  trailing?: React.ReactNode;
  disabled?: boolean;
}

/**
 * Full-card selectable radio option. Selected state uses the primary tint.
 * Shared across the bot settings surfaces (Book Demo, Human Handoff).
 */
const RadioCard: React.FC<IRadioCardProps> = ({
  checked, onSelect, title, subtitle, trailing, disabled,
}) => (
  <button
    type="button"
    role="radio"
    aria-checked={checked}
    disabled={disabled}
    onClick={onSelect}
    className={cn(
      'group flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors',
      checked
        ? 'border-primary bg-primary/[0.06] ring-1 ring-inset ring-primary/40'
        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50',
      disabled && 'cursor-not-allowed opacity-60',
    )}
  >
    <span
      className={cn(
        'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-2 transition-colors',
        checked ? 'border-primary' : 'border-gray-300 group-hover:border-gray-400',
      )}
    >
      {checked && <span className="h-2 w-2 rounded-full bg-primary" />}
    </span>
    <span className="min-w-0 flex-1">
      <span className="block truncate text-sm font-semibold text-gray-900">{title}</span>
      {subtitle && <span className="mt-0.5 block truncate text-[13px] text-gray-500">{subtitle}</span>}
    </span>
    {trailing && <span className="shrink-0 pl-2">{trailing}</span>}
  </button>
);

export default RadioCard;
